import { supabase } from '../supabase';
import { increaseCouponCountIfAny } from './coupons';

// Define interfaces for the payment data
interface PaymentResponse {
  payment: {
    id: string;
    status: string;
    status_detail: string;
    transaction_amount: number;
    payment_method_id: string;
    payment_type_id: string;
    date_approved: string;
  };
}

export const createPaymentToken = async (
  token: string,
  amount: number,
  email: string,
  identificationType: string,
  identificationNumber: string,
  customerId?: string
) => {
  const { data, error } = await supabase
    .from('payment_tokens')
    .insert({
      token,
      amount,
      email,
      identification_type: identificationType,
      identification_number: identificationNumber,
      customer_id: customerId,
      expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Token expires in 24 hours
    })
    .select()
    .single();

  if (error) {
    throw new Error(`Error storing payment token: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from payment_tokens insert');
  }

  return data.id;
};

export async function capturePayment(taskId: string, askerId: string) {
  console.log(`Capturing payment for taskId: ${taskId}, askerId: ${askerId}`);

  // Fetch task information
  console.log(`Fetching task information for taskId: ${taskId}`);
  const { data: task, error: taskError } = await supabase
    .from('tasks')
    .select('payment_token_id, asker_id')
    .eq('id', taskId)
    .single();

  if (taskError) {
    console.error(`Error fetching task: ${taskError.message}`);
    throw new Error(`Error fetching task: ${taskError.message}`);
  }

  if (!task || !task.payment_token_id) {
    console.error('Task or payment token not found');
    throw new Error('Task or payment token not found');
  }

  console.log(`Task found: ${JSON.stringify(task)}`);

  // Fetch payment token information
  console.log(
    `Fetching payment token information for token_id: ${task.payment_token_id}`
  );
  const { data: paymentToken, error: tokenError } = await supabase
    .from('payment_tokens')
    .select('*')
    .eq('id', task.payment_token_id)
    .single();

  if (tokenError) {
    console.error(`Error fetching payment token: ${tokenError.message}`);
    throw new Error(`Error fetching payment token: ${tokenError.message}`);
  }

  if (!paymentToken) {
    console.error('Payment token not found');
    throw new Error('Payment token not found');
  }

  console.log(`Payment token found: ${JSON.stringify(paymentToken)}`);

  // Prepare the payload for the Edge Function
  console.log('Preparing payload for Edge Function');
  console.log('paymentToken', paymentToken);
  console.log('token', task.payment_token_id);
  console.log('customer_id', paymentToken.customer_id);
  const payload = {
    token: paymentToken.token,
    transaction_amount: paymentToken.amount,
    description: `Payment for Task ${taskId}`,
    installments: 1,
    payment_method_id: paymentToken.payment_method_id,
    payer: {
      email: paymentToken.email,
      identification: {
        type: paymentToken.identification_type,
        number: paymentToken.identification_number,
      },
      id: paymentToken.customer_id,
    },
  };
  console.log(`Prepared payload: ${JSON.stringify(payload)}`);

  // Declare and initialize paymentData and paymentError
  let paymentData: PaymentResponse | null = null;
  let paymentError: Error | null = null;

  // Call the Edge Function
  console.log('Calling Edge Function: captureAuthorizedPayment');
  const { data, error } = await supabase.functions.invoke<PaymentResponse>(
    'captureAuthorizedPayment',
    {
      body: JSON.stringify(payload),
    }
  );

  if (error) {
    console.error(`Error capturing payment: ${error.message}`);
    paymentError = error;
  } else if (data) {
    console.log(`Payment captured successfully: ${JSON.stringify(data)}`);
    paymentData = data;
  }

  increaseCouponCountIfAny(Number(taskId));

  // Insert record into asker_payments table (for both success and failure)
  console.log(`Inserting payment record for task_id: ${taskId}`);
  const { data: paymentRecord, error: insertError } = await supabase
    .from('asker_payments')
    .insert({
      task_id: taskId,
      asker_id: askerId,
      amount: paymentData?.payment.transaction_amount ?? paymentToken.amount,
      payment_id: paymentData?.payment.id ?? null,
      payment_status: paymentData?.payment.status ?? 'failed',
      payment_status_detail:
        paymentData?.payment.status_detail ?? paymentError?.message,
      payment_method:
        paymentData?.payment.payment_method_id ??
        paymentToken.payment_method_id,
      payment_type: paymentData?.payment.payment_type_id ?? null,
      created_at:
        paymentData?.payment.date_approved ?? new Date().toISOString(),
    })
    .single();

  if (insertError) {
    console.error(`Error inserting payment record: ${insertError.message}`);
    throw new Error(`Error inserting payment record: ${insertError.message}`);
  }

  console.log(
    `Payment record inserted successfully: ${JSON.stringify(paymentRecord)}`
  );

  if (paymentError) {
    throw new Error(`Error capturing payment: ${paymentError.message}`);
  }

  return paymentRecord;
}
